/**
 * freshness.js - Data freshness computation for all comparator verticals.
 *
 * Thresholds:
 *   <=60 days  → verified   "Verificado <Mes YYYY>"
 *   61–120 days → pending   "Actualización pendiente"
 *   >120 / null → unverified "Sin verificación"
 */

const MONTH_NAMES_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/**
 * Compute freshness status for a single item.
 * @param {string|null} dateStr  ISO date string (e.g. "2026-02-23")
 * @returns {{ status: 'verified'|'pending'|'unverified', label: string, daysSince: number|null }}
 */
export function computeFreshness(dateStr) {
  if (!dateStr) {
    return { status: 'unverified', label: 'Sin verificación', daysSince: null };
  }

  const verifiedDate = new Date(dateStr);
  const now = new Date();
  const diffMs = now - verifiedDate;
  const daysSince = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (daysSince <= 60) {
    const month = MONTH_NAMES_ES[verifiedDate.getMonth()];
    const year = verifiedDate.getFullYear();
    return {
      status: 'verified',
      label: `Verificado ${month} ${year}`,
      daysSince,
    };
  }

  if (daysSince <= 120) {
    return { status: 'pending', label: 'Actualización pendiente', daysSince };
  }

  return { status: 'unverified', label: 'Sin verificación', daysSince };
}

/**
 * Map our freshness status to the FreshnessBadge status prop
 * (which expects 'verified' | 'recent' | 'needs_review').
 */
export function toFreshnessBadgeStatus(freshnessStatus) {
  switch (freshnessStatus) {
    case 'verified': return 'verified';
    case 'pending': return 'recent';      // maps to yellow ⚠️
    case 'unverified': return 'needs_review'; // maps to neutral
    default: return 'needs_review';
  }
}

/**
 * Compute the global freshness date for a vertical.
 * Prefers a vertical-level _meta.last_verified_at, otherwise
 * falls back to max(items[].data_ops.last_verified_at).
 *
 * @param {Array} items  Normalised item array
 * @param {Object|null} rawSection  The raw JSON section (e.g. json.bank_accounts)
 * @returns {{ dateStr: string|null, label: string }}
 */
export function getVerticalFreshnessDate(items, rawSection) {
  // 1. Check vertical-level meta
  const metaDate = rawSection?._meta?.last_verified_at
    || rawSection?._meta?.rates_as_of;
  if (metaDate) {
    return { dateStr: metaDate, label: formatMonthYear(metaDate) };
  }

  // 2. Compute from items
  let maxDate = null;
  for (const item of items) {
    const d = item.data_ops?.last_verified_at;
    if (d && (!maxDate || d > maxDate)) {
      maxDate = d;
    }
  }

  if (maxDate) {
    return { dateStr: maxDate, label: formatMonthYear(maxDate) };
  }

  return { dateStr: null, label: 'fecha no disponible' };
}

/**
 * Format an ISO date string to "febrero 2026".
 */
export function formatMonthYear(dateStr) {
  if (!dateStr) return 'fecha no disponible';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr; // fallback to raw string
  return `${MONTH_NAMES_ES[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Compute the ISO date string for SEO dateModified.
 * Same logic as getVerticalFreshnessDate but returns raw dateStr only.
 */
export function getSEODateModified(items, rawSection) {
  return getVerticalFreshnessDate(items, rawSection).dateStr || null;
}
